import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LifeBuoy, Send, CheckCircle, Clock, Trash2, ExternalLink, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import EditFatturaModal from "@/components/EditFatturaModal";

const CATEGORIES = [
  { key: "fattura_errata", label: "Fattura errata" },
  { key: "ore_sbagliate", label: "Ore sbagliate" },
  { key: "generico", label: "Richiesta generica" },
  { key: "altro", label: "Altro" },
];

export default function Support() {
  const { user } = useAuth();
  const isDirettore = user?.role === "Proprietario";
  const [tab, setTab] = useState(isDirettore ? "manage" : "mine");
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "generico", fattura_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [editFattura, setEditFattura] = useState(null);
  const [myFatture, setMyFatture] = useState([]);

  const loadMine = useCallback(() => {
    setLoading(true);
    api.get("/tickets/mine").then((r) => setMine(r.data)).finally(() => setLoading(false));
  }, []);

  const loadAll = useCallback(() => {
    setLoading(true);
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/tickets${q}`).then((r) => setAll(r.data)).finally(() => setLoading(false));
  }, [filter]);

  const loadMyFatture = useCallback(() => {
    api.get("/fatture/mine?scope=lifetime").then((r) => setMyFatture(r.data.fatture || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadMine();
    loadMyFatture();
    if (isDirettore) loadAll();
  }, [loadMine, loadMyFatture, loadAll, isDirettore]);

  useEffect(() => {
    if (isDirettore && tab === "manage") loadAll();
  }, [filter, tab, isDirettore, loadAll]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Oggetto e messaggio obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/tickets", { ...form, fattura_id: form.fattura_id || null });
      toast.success("Ticket inviato · il Proprietario riceverà una notifica");
      setForm({ subject: "", message: "", category: "generico", fattura_id: "" });
      setOpenForm(false);
      loadMine();
      if (isDirettore) loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const closeTicket = async (id) => {
    try {
      await api.post(`/tickets/${id}/close`);
      toast.success("Ticket chiuso");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Eliminare questo ticket?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket eliminato");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditFattura = async (fattura_id) => {
    try {
      const { data } = await api.get(`/fatture/${fattura_id}`);
      setEditFattura(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="w-full">
      <PageHeader
        icon={LifeBuoy}
        title="Supporto"
        subtitle={isDirettore ? "Gestisci i ticket dei dipendenti e correggi le fatture." : "Segnala errori o richiedi assistenza al Proprietario."}
        right={
          isDirettore ? (
            <div className="flex flex-wrap items-center gap-1 ot-panel p-1">
              {[
                { k: "manage", l: "Gestione" },
                { k: "mine", l: "Miei" },
              ].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`supp-tab-${t.k}`}
                  className={`px-3 py-1.5 text-xs ot-heading uppercase tracking-widest rounded transition-colors ${tab === t.k ? "bg-blue-400 text-black" : "text-slate-400 hover:text-blue-400"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          ) : null
        }
      />

      {tab === "mine" && (
        <>
          <button
            data-testid="open-ticket-form"
            onClick={() => setOpenForm(!openForm)}
            className="ot-btn-primary mb-4 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {openForm ? "Chiudi form" : "Apri nuovo ticket"}
          </button>

          {openForm && (
            <form onSubmit={submit} className="ot-panel-elev p-6 mb-6 space-y-4 ot-fade-up">
              <div>
                <label className="ot-label block mb-1.5">Categoria</label>
                <select data-testid="ticket-category" className="ot-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              {form.category === "fattura_errata" && (
                <div>
                  <label className="ot-label block mb-1.5">Fattura di riferimento (opzionale)</label>
                  <select data-testid="ticket-fattura" className="ot-input" value={form.fattura_id} onChange={(e) => setForm({ ...form, fattura_id: e.target.value })}>
                    <option value="">-- Seleziona una tua fattura --</option>
                    {myFatture.map((f) => (
                      <option key={f.id} value={f.id}>
                        {formatDate(f.created_at)} · {f.vehicle_name || "Fattura"} · ${Math.round(f.total || 0).toLocaleString("it-IT")}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="ot-label block mb-1.5">Oggetto</label>
                <input data-testid="ticket-subject" className="ot-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Es. Ho sbagliato la quantità del FullKit" required />
              </div>
              <div>
                <label className="ot-label block mb-1.5">Messaggio</label>
                <textarea data-testid="ticket-message" className="ot-input min-h-[120px] resize-y" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Descrivi il problema..." required />
              </div>
              <div className="flex gap-2">
                <button data-testid="ticket-submit" type="submit" disabled={submitting} className="ot-btn-primary flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  {submitting ? "Invio..." : "Invia Ticket"}
                </button>
                <button type="button" onClick={() => setOpenForm(false)} className="ot-btn-ghost">Annulla</button>
              </div>
            </form>
          )}

          <div className="ot-panel-elev p-6">
            <h2 className="ot-heading text-2xl font-bold text-slate-100 mb-4">I miei Ticket</h2>
            {mine.length === 0 ? (
              <div className="py-12 text-center text-slate-500">Nessun ticket aperto.</div>
            ) : (
              <div className="space-y-2">
                {mine.map((t) => <TicketCard key={t.id} t={t} />)}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "manage" && isDirettore && (
        <>
          <div className="flex items-center gap-1 ot-panel p-1 w-max mb-4">
            {[
              { k: "open", l: "Aperti" },
              { k: "closed", l: "Chiusi" },
              { k: "all", l: "Tutti" },
            ].map((s) => (
              <button key={s.k} onClick={() => setFilter(s.k)} data-testid={`ticket-filter-${s.k}`}
                className={`px-3 py-1.5 text-xs ot-heading uppercase tracking-widest rounded transition-colors ${filter === s.k ? "bg-blue-400 text-black" : "text-slate-400 hover:text-blue-400"}`}>
                {s.l}
              </button>
            ))}
          </div>

          <div className="ot-panel-elev p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="ot-heading text-2xl font-bold text-slate-100">Gestione Ticket</h2>
              <span className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">{all.length} TICKET</span>
            </div>
            {loading ? (
              <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
            ) : all.length === 0 ? (
              <div className="py-12 text-center text-slate-500">Nessun ticket {filter === "open" ? "aperto" : filter === "closed" ? "chiuso" : ""}.</div>
            ) : (
              <div className="space-y-3">
                {all.map((t) => (
                  <TicketCard
                    key={t.id}
                    t={t}
                    isDirettore
                    onClose={() => closeTicket(t.id)}
                    onDelete={() => deleteTicket(t.id)}
                    onEditFattura={t.fattura_id ? () => openEditFattura(t.fattura_id) : null}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {editFattura && (
        <EditFatturaModal
          fattura={editFattura}
          onClose={() => setEditFattura(null)}
          onSaved={() => { setEditFattura(null); if (isDirettore) loadAll(); }}
        />
      )}
    </div>
  );
}

function TicketCard({ t, isDirettore, onClose, onDelete, onEditFattura }) {
  const isOpen = t.status === "open";
  return (
    <div className={`ot-panel p-4 ${isOpen ? "border-amber-500/40" : ""}`} data-testid={`ticket-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`ot-mono text-[0.6rem] tracking-widest border rounded px-1.5 py-0.5 ${isOpen ? "text-amber-400 border-amber-400/40" : "text-emerald-400 border-emerald-400/40"}`}>
              {isOpen ? <><Clock className="w-2.5 h-2.5 inline mr-1" />APERTO</> : <><CheckCircle className="w-2.5 h-2.5 inline mr-1" />CHIUSO</>}
            </span>
            <span className="ot-mono text-[0.6rem] text-blue-400/80 tracking-widest border border-blue-400/30 rounded px-1.5 py-0.5">{t.category?.toUpperCase()}</span>
            {t.fattura_id && (
              <span className="ot-mono text-[0.6rem] text-blue-400 tracking-widest border border-blue-400/40 rounded px-1.5 py-0.5">FATTURA REF</span>
            )}
          </div>
          <div className="ot-heading font-bold text-slate-100 uppercase text-sm mb-1">{t.subject}</div>
          <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{t.message}</div>
          <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest mt-2">
            Da <span className="text-blue-400">@{t.username}</span> · {formatDate(t.created_at)}
            {t.resolved_at && ` · Chiuso da @${t.resolved_by} ${formatDate(t.resolved_at)}`}
          </div>
        </div>
        {isDirettore && (
          <div className="flex flex-col gap-1.5 shrink-0">
            {onEditFattura && (
              <button onClick={onEditFattura} data-testid={`edit-fattura-from-ticket-${t.id}`} className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1 text-xs !text-blue-400 !border-blue-400/40">
                <ExternalLink className="w-3 h-3" />
                Modifica fattura
              </button>
            )}
            {isOpen && (
              <button onClick={onClose} data-testid={`close-ticket-${t.id}`} className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1 text-xs !text-emerald-400 !border-emerald-400/40">
                <CheckCircle className="w-3 h-3" />
                Chiudi
              </button>
            )}
            <button onClick={onDelete} data-testid={`delete-ticket-${t.id}`} className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1 text-xs !text-red-400 !border-red-500/40">
              <Trash2 className="w-3 h-3" />
              Elimina
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
